<div class="dues-page">
  <header class="dues-hero">
    <div class="dues-hero__copy">
      <p class="dues-kicker">Collections</p>
      <h1 class="dues-title">Reception Dues</h1>
      <p class="dues-subtitle">
        Patients with unpaid balances still open
        <strong>{{ graceDays }}+ days</strong> after consultation.
      </p>
    </div>

    <div class="dues-hero__actions">
      <button
        type="button"
        class="refresh-btn"
        (click)="refreshNotifications()"
        [disabled]="loading"
        [attr.aria-busy]="loading">
        <i class="pi pi-refresh" [class.is-spinning]="loading"></i>
        {{ loading ? 'Refreshing…' : 'Refresh' }}
      </button>
    </div>
  </header>

  <section class="dues-stats" aria-label="Due summary">
    <article class="stat-card stat-card--cases">
      <div class="stat-card__icon"><i class="pi pi-users"></i></div>
      <div class="stat-card__body">
        <span class="stat-label">Pending cases</span>
        <span class="stat-value">{{ filteredNotifications.length }}</span>
      </div>
    </article>

    <article class="stat-card stat-card--money">
      <div class="stat-card__icon"><i class="pi pi-wallet"></i></div>
      <div class="stat-card__body">
        <span class="stat-label">Total due</span>
        <span class="stat-value">{{ totalDuesAmount | currency:'INR':'symbol':'1.0-0' }}</span>
      </div>
    </article>

    <article class="stat-card stat-card--oldest">
      <div class="stat-card__icon"><i class="pi pi-clock"></i></div>
      <div class="stat-card__body">
        <span class="stat-label">Oldest pending</span>
        <span class="stat-value">
          {{ oldestPendingDays }}
          <small>days</small>
        </span>
      </div>
    </article>

    <article class="stat-card stat-card--high">
      <div class="stat-card__icon"><i class="pi pi-arrow-up-right"></i></div>
      <div class="stat-card__body">
        <span class="stat-label">Highest due</span>
        <span class="stat-value">{{ highestDueAmount | currency:'INR':'symbol':'1.0-0' }}</span>
      </div>
    </article>
  </section>

  <section class="dues-toolbar">
    <div class="band-filters" role="tablist" aria-label="Filter by days pending">
      <button
        type="button"
        role="tab"
        class="band-chip"
        [class.is-active]="activeBand === 'all'"
        (click)="setBand('all')">
        All
        <span class="band-count">{{ bandCounts.all }}</span>
      </button>
      <button
        type="button"
        role="tab"
        class="band-chip band-chip--fresh"
        [class.is-active]="activeBand === 'fresh'"
        (click)="setBand('fresh')">
        15–29 days
        <span class="band-count">{{ bandCounts.fresh }}</span>
      </button>
      <button
        type="button"
        role="tab"
        class="band-chip band-chip--aging"
        [class.is-active]="activeBand === 'aging'"
        (click)="setBand('aging')">
        30–59 days
        <span class="band-count">{{ bandCounts.aging }}</span>
      </button>
      <button
        type="button"
        role="tab"
        class="band-chip band-chip--critical"
        [class.is-active]="activeBand === 'critical'"
        (click)="setBand('critical')">
        60+ days
        <span class="band-count">{{ bandCounts.critical }}</span>
      </button>
    </div>

    <div class="search-wrap">
      <i class="pi pi-search" aria-hidden="true"></i>
      <input
        type="search"
        pInputText
        placeholder="Search patient or reason…"
        [value]="searchTerm"
        (input)="onSearch($event)"
        class="search-input"
        aria-label="Search dues" />
      <button
        type="button"
        class="search-clear-btn"
        *ngIf="searchTerm"
        (click)="clearSearch()"
        aria-label="Clear search">
        <i class="pi pi-times"></i>
      </button>
    </div>
  </section>

  <section class="dues-table-shell">
    <p-table
      [value]="filteredNotifications"
      [loading]="loading"
      [paginator]="filteredNotifications.length > 10"
      [rows]="10"
      styleClass="p-datatable-sm dues-table"
      [globalFilterFields]="['patient_name','reason']"
      responsiveLayout="scroll"
      sortField="days_pending"
      [sortOrder]="-1">

      <ng-template pTemplate="header">
        <tr>
          <th pSortableColumn="patient_name" style="min-width: 11rem">
            Patient <p-sortIcon field="patient_name"></p-sortIcon>
          </th>
          <th pSortableColumn="consultation_date" style="min-width: 8.5rem">
            Consultation <p-sortIcon field="consultation_date"></p-sortIcon>
          </th>
          <th pSortableColumn="days_pending" style="min-width: 7.5rem">
            Days pending <p-sortIcon field="days_pending"></p-sortIcon>
          </th>
          <th pSortableColumn="due_amount" style="min-width: 7rem">
            Due amount <p-sortIcon field="due_amount"></p-sortIcon>
          </th>
          <th pSortableColumn="reason" style="min-width: 8rem">
            Reason <p-sortIcon field="reason"></p-sortIcon>
          </th>
          <th style="width: 8.5rem">Action</th>
        </tr>
      </ng-template>

      <ng-template pTemplate="body" let-row let-rowIndex="rowIndex">
        <tr class="dues-row" [style.animation-delay.ms]="rowIndex * 35">
          <td>
            <div class="patient-cell">
              <span class="patient-avatar" aria-hidden="true">{{ patientInitials(row.patient_name) }}</span>
              <div class="patient-meta">
                <span class="patient-name">{{ row.patient_name || '—' }}</span>
                <span class="patient-id" *ngIf="row.patient_id">#{{ row.patient_id }}</span>
              </div>
            </div>
          </td>

          <td class="date-cell">
            {{ row.consultation_date ? (row.consultation_date | date:'dd MMM yyyy') : '—' }}
          </td>

          <td>
            <span
              class="days-chip"
              [ngClass]="'days-chip--' + urgencyLevel(row.days_pending)"
              [pTooltip]="row.follow_up_date ? ('Due window started ' + (row.follow_up_date | date:'dd MMM yyyy')) : undefined"
              tooltipPosition="top">
              {{ row.days_pending }}d
            </span>
          </td>

          <td class="due-amount">
            {{ row.due_amount | currency:'INR':'symbol':'1.0-0' }}
          </td>

          <td>
            <span class="reason-pill">{{ row.reason || 'Personal Due' }}</span>
          </td>

          <td>
            <button
              type="button"
              pButton
              class="settle-due-btn"
              (click)="openDuesSettlement(row)"
              [pTooltip]="'Settle due for ' + row.patient_name"
              tooltipPosition="left">
              <i class="pi pi-wallet"></i>
              <span>Settle</span>
            </button>
          </td>
        </tr>
      </ng-template>

      <ng-template pTemplate="emptymessage">
        <tr>
          <td colspan="6">
            <div class="empty-state" *ngIf="!loading">
              <div class="empty-state__icon"><i class="pi pi-check-circle"></i></div>
              <h2 class="empty-state__title">
                {{ searchTerm || activeBand !== 'all' ? 'No matching dues' : 'All caught up' }}
              </h2>
              <p class="empty-state__text">
                {{
                  searchTerm || activeBand !== 'all'
                    ? 'Try another search or clear the day filter.'
                    : 'No patients have unpaid balances past ' + graceDays + ' days since consultation.'
                }}
              </p>
              <button
                type="button"
                class="empty-state__clear"
                *ngIf="searchTerm || activeBand !== 'all'"
                (click)="clearFilters()">
                Clear filters
              </button>
            </div>
          </td>
        </tr>
      </ng-template>
    </p-table>
  </section>
</div>
